import React, { useState, useEffect, useMemo, use } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

// CSS
import "../0PageStyles.css";
import "./noteViewPage.css";

// Components
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import ItemView from "../../Components/Item Components/itemView";
import PageLoader from "../../Components/Other Components/pageLoader";
import Title from "../../Components/Other Components/Title";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import PrinterComponent from "../../Components/Other Components/PrinterComponent";
import BarcodeDisplayComponent from "../../Components/Other Components/BarcodeDisplayComponent";

// Functions and hooks
import { GetNoteData, DeleteNote } from "../../BackendAccess/TrafficHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { formatCurrency, useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";
import { generateItemsQRCodeValue } from "../../hooks/qrCodeCreator";

export default function NoteViewPage() {
  const [ID, setID] = useState();
  const [Type, setType] = useState();
  const [Warehouse, setWarehouse] = useState();
  const [Client, setClient] = useState();
  const [Price, setPrice] = useState();
  const [Discount, setDiscount] = useState();
  const [DateTime, setDateTime] = useState();
  const [Comment, setComment] = useState();
  const [itemArray, setItemArray] = useState(null);
  const [LastEditedBy, setLastEditedBy] = useState();
  const [WarehouseID, setWarehouseID] = useState(-1);
  const [ClientID, setClientID] = useState(-1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { id: currentID } = location.state;

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const { companySettings, refreshCompanySettings } = useSettings();

  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  const showConfirmPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to delete this note?", { ns: "traffic" })}
            <br />{" "}
            {t(
              "All the item amounts tied to this note will be taken out of/added to the warehouse tied to the note!",
              { ns: "traffic" }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => handleConfirmDelete(key)}
        onCancel={() => handleCancelDelete(key)}
      />,

      key
    );
  };

  ////////Load data ///////////////////////////////////////
  const GetAndLoadData = async () => {
    setLoading(true);
    try {
      await refreshCompanySettings();

      const [data, error] = await fetchWithTimeout(() =>
        GetNoteData(currentID)
      );
      console.log("Traffic data", data);

      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        LoadData(data);
      }
    } catch (err) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentID) {
      GetAndLoadData();
    }
  }, [currentID]);

  const LoadData = (info) => {
    setID(info.TrafficID);
    setType(info.Type);
    setWarehouse(info.WarehouseName + " (" + info.Warehouse + ")");
    setClient(info.ClientName + " (" + info.Client + ")");
    setWarehouseID(info.Warehouse);
    setClientID(info.Client);
    setPrice(info.Price);
    setDiscount(info.Discount);
    setDateTime(info.DateTime);
    setLastEditedBy(
      info.FirstName + " " + info.LastName + " (" + info.LastEditedBy + ")"
    );
    setComment(info.Comment);
  };
  ////////Load data END///////////////////////////////////////

  const handleConfirmDelete = async (key) => {
    setDeleteBtnDisabled(true);
    showNotification(t("Deleting note...", { ns: "traffic" }), "info");

    try {
      const [data, error] = await DeleteNote(currentID);
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Note deleted successfully!", { ns: "traffic" }),
          "success"
        );
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setDeleteBtnDisabled(false);
      removePopup(key);
      navigate(-1);
    }
  };
  const clickOnDelete = () => {
    showConfirmPopup();
  };
  const handleCancelDelete = (key) => {
    removePopup(key);
  };
  const clickOnCancel = () => {
    navigate(-1);
  };
  const handlePrintSuccess = () => {};

  const handlePrintError = (error) => {};

  const documentData = useMemo(() => {
    return {
      noteId: ID,
      date: DateTime,
      type: t(Type),
      client: Client,
      warehouse: Warehouse,
      items: itemArray?.map((item) => {
        return {
          Name: item.Name,
          Amount: parseInt(item.Amount),
          Price: parseFloat(item.Price),
        };
      }),
      notes: Comment,
      createdBy: LastEditedBy,
    };
  }, [ID, DateTime, Type, Client, Warehouse, itemArray, Comment, LastEditedBy]);

  useEffect(() => {
    if (itemArray) {
      makeQRCode();
    }
  }, [itemArray]);

  const [QRcode, setQRCode] = useState(0);
  const makeQRCode = () => {
    let itemsMap = new Map();
    itemArray?.forEach((item) => {
      if (item.Amount > 0) {
        itemsMap.set(item.ItemID, item.Amount);
      }
    });
    const qrJson = generateItemsQRCodeValue(itemsMap, WarehouseID, ClientID);
    setQRCode(qrJson);
    return qrJson;
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Viewing a note", { ns: "tutorial" }),
      message: t(
        "This page shows one traffic note's full details. Notes are permanent records - none of the fields here can be edited, but you can print it, show its QR code, or delete it entirely.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-view-details",
      title: t("Note details", { ns: "tutorial" }),
      message: t(
        "The note's type, warehouse, client, price, discount, and the date and time it was created.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".item-view-container",
      title: t("Items on this note", { ns: "tutorial" }),
      message: t(
        "Every item this note covers, along with the quantity that was moved.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-view-print-btn",
      title: t("Print", { ns: "tutorial" }),
      message: t(
        "Print this note as a document, or show its QR code for quick scanning.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "note-view-delete-btn",
      title: t("Delete", { ns: "tutorial" }),
      message: t(
        "Permanently deletes this note. All the stock it moved will be reversed - taken out of or added back to the warehouse. This cannot be undone.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="noteView" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content ">
          <Title className="mt-3">
            {t("Note ID", { ns: "traffic" }) + ": " + ID}
          </Title>

          <div data-tutorial-id="note-view-details">
          <InputGroup className="container-bordered w-90 vertical mb-4 mt-4 h-auto">
            <InputField
              className="mb-3"
              placeholder={t("Example: 1001")}
              label={t("ID") + ":"}
              value={ID}
              onChange={() => {}}
              readOnly={true}
            />
            <InputField
              className="mb-3"
              placeholder={t("Example: Delivery note")}
              label={t("Note type") + ":"}
              value={t(Type)}
              onChange={() => {}}
              readOnly={true}
            />
            <InputField
              className="mb-3"
              placeholder={t("Example: Warehouse 1")}
              label={t("Warehouse") + ":"}
              value={Warehouse}
              onChange={() => {}}
              readOnly={true}
            />
            <InputField
              className="mb-3"
              placeholder={t("Example: Client 1")}
              label={t("Client") + ":"}
              value={Client}
              onChange={() => {}}
              readOnly={true}
            />
            <InputField
              className="mb-3"
              placeholder={t("Example: 120.20")}
              label={t("Price") + ":"}
              value={formatCurrency(Price, companySettings.currency)}
              onChange={() => {}}
              readOnly={true}
            />
            <InputField
              className="mb-3"
              placeholder={t("Example: 10", { ns: "traffic" })}
              label={t("Discount (in %)", { ns: "traffic" }) + ":"}
              value={Discount}
              onChange={() => {}}
              readOnly={true}
            />
            <InputField
              className="mb-3"
              placeholder={t("Example: 2021/12/31 23:59:59", { ns: "traffic" })}
              label={t("Date & Time", { ns: "traffic" }) + ":"}
              value={DateTime}
              onChange={() => {}}
              readOnly={true}
            />
          </InputGroup>
          </div>
          <ItemView
            bEnableCreate={false}
            bEnableEdit={false}
            bNoteView={true}
            noteType={Type}
            NoteID={ID ? ID : -2}
            setItemsOutsideView={setItemArray}
          ></ItemView>
          <div className="empty-container ninety-perc">
            <InputField
              className="mb-2"
              placeholder={t("Delivered successfully!", { ns: "traffic" })}
              label={t("Comment") + ":"}
              value={Comment}
              inputMargins={["0", "0", "1rem", "0"]}
              labelWidth={"7rem"}
              readOnly={true}
              largeField={true}
            ></InputField>
          </div>
          <span className="last-edited-by-bar w-90">
            <span className="sec-textcolor-span">{t("Created by:")} </span>
            &nbsp;
            {LastEditedBy}
          </span>
          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>

            <div data-tutorial-id="note-view-print-btn">
              <PrinterComponent
                documentData={documentData}
                onPrintSuccess={handlePrintSuccess}
                onPrintError={handlePrintError}
                title="Traffic Note"
                codeValue={QRcode}
              />
            </div>

            <BarcodeDisplayComponent value={QRcode} onClick={makeQRCode} />
            <Button
              btnMargin={"btn-default"}
              icon="delete"
              btnStyle="btn-negative"
              onClick={clickOnDelete}
              disabled={deleteBtnDisabled}
              tutorialId="note-view-delete-btn"
            >
              {t("Delete")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
